import { Asset, SortOrder } from '@/api/generatedApi';
import { DependencyFilter, DependencySortField } from '@/api/dependencyTypes';
import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createUseStyles } from '@/services/createUseStyles';
import { SimpleTreeView } from '@mui/x-tree-view';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { DependencyTreeView, TreeItemWrapper, DependencyContext } from './DependencyTreeView';
import { DependencyTreeToolbar } from './DependencyTreeToolbar';

const useStyles = createUseStyles({
  DependencyTreeContainer: {
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
});

export interface DependencyTreeContainerProperties {
  asset: Asset;
  onTreeItemClick?: (itemId: string, context?: DependencyContext, selectionPath?: string[]) => void;
  selectedSelectionPath?: string[];
  readOnly?: boolean;
}

const defaultOrderFor = (sortBy: DependencySortField): SortOrder =>
  sortBy === DependencySortField.Status || sortBy === DependencySortField.Occurrences ? SortOrder.DESC : SortOrder.ASC;

export const DependencyTreeContainer = ({
  asset,
  onTreeItemClick,
  selectedSelectionPath,
  readOnly,
}: DependencyTreeContainerProperties) => {
  const c = useStyles();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const expandedItemIds = useMemo(() => new Set(expandedItems), [expandedItems]);
  const [filter, setFilter] = useState<DependencyFilter>(DependencyFilter.All);
  const [sortBy, setSortBy] = useState<DependencySortField>(DependencySortField.Status);
  const [sortOrder, setSortOrder] = useState<SortOrder>(defaultOrderFor(DependencySortField.Status));

  const revealAncestor = useCallback((itemId: string) => {
    setExpandedItems(previous => (previous.includes(itemId) ? previous : [...previous, itemId]));
  }, []);

  const hasRevealTarget = Boolean(selectedSelectionPath?.length);

  useEffect(() => {
    if (hasRevealTarget) revealAncestor(asset.id);
  }, [hasRevealTarget, asset.id, revealAncestor]);

  const handleSortByChange = (next: DependencySortField) => {
    setSortBy(next);
    setSortOrder(defaultOrderFor(next));
  };

  return (
    <Box data-cy='assembly-tree-wrapper' className={c.DependencyTreeContainer}>
      <DependencyTreeToolbar
        filter={filter}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onFilterChange={setFilter}
        onSortByChange={handleSortByChange}
        onSortOrderChange={setSortOrder}
      />
      <SimpleTreeView
        key={asset.id}
        aria-label='assembly tree'
        data-cy='assembly-tree'
        disableSelection
        expandedItems={expandedItems}
        onExpandedItemsChange={(_event, itemIds) => setExpandedItems(itemIds)}
        slots={{ expandIcon: ChevronRight, collapseIcon: ExpandMore }}
        sx={{
          '& .MuiTreeItem-iconContainer': {
            color: 'text.primary',
            width: 'auto',
            minWidth: 'auto',
            '& svg': { fontSize: '1.25rem' },
            '& svg.missing-dep-icon': { fontSize: '14px' },
          },
          '& .MuiTreeItem-groupTransition': { ml: 1, pl: 1 },
        }}
      >
        <TreeItemWrapper id={asset.id} path={asset.path} hasMissingChildDependencies={asset.isMissingDependencies}>
          <DependencyTreeView
            asset={asset}
            onTreeItemClick={onTreeItemClick}
            selectedSelectionPath={selectedSelectionPath}
            onRevealAncestor={revealAncestor}
            expandedItems={expandedItemIds}
            filter={filter}
            sortBy={sortBy}
            sortOrder={sortOrder}
            readOnly={readOnly}
          />
        </TreeItemWrapper>
      </SimpleTreeView>
    </Box>
  );
};
